import { useState, useEffect } from 'react'
import { useAuth } from '../../auth/AuthContext'
import { MemberSearchAdd, MemberAvatar, OrgMember } from '../sharing/MemberSearchAdd'
import type { FolderVisibility } from '../../types/models'

interface FolderAclModalProps {
  folderId: string
  folderName: string
  currentAcl?: {
    owners: string[]
    editors: string[]
    viewers: string[]
  }
  currentVisibility?: FolderVisibility
  onClose: () => void
  onSuccess: () => void
}

type FolderRole = 'owner' | 'editor' | 'viewer'

interface AccessEntry extends OrgMember {
  role: FolderRole
}

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

export function FolderAclModal({ folderId, folderName, currentAcl, currentVisibility, onClose, onSuccess }: FolderAclModalProps) {
  const { user, firebaseUser } = useAuth()
  const [members, setMembers] = useState<OrgMember[]>([])
  const [entries, setEntries] = useState<AccessEntry[]>([])
  // 未設定（従来）のフォルダは組織全体公開相当として表示
  const [visibility, setVisibility] = useState<FolderVisibility>(currentVisibility || 'org')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    loadMembers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadMembers = async () => {
    try {
      if (!firebaseUser || !user?.orgId) return

      const token = await firebaseUser.getIdToken()
      const response = await fetch(`${API_BASE}/v1/organizations/${user.orgId}/members`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) throw new Error('Failed to load members')

      const data = await response.json()
      const orgMembers: OrgMember[] = data.members.map((m: any) => ({
        uid: m.uid,
        email: m.email,
        displayName: m.displayName || m.email
      }))
      setMembers(orgMembers)

      // 現在アクセス権を持つメンバーのみリスト化
      const initial: AccessEntry[] = []
      const pushRole = (uids: string[] | undefined, role: FolderRole) => {
        for (const uid of uids || []) {
          if (initial.some(e => e.uid === uid)) continue
          const m = orgMembers.find(om => om.uid === uid)
          initial.push({
            uid,
            email: m?.email || uid,
            displayName: m?.displayName || m?.email || uid,
            role
          })
        }
      }
      pushRole(currentAcl?.owners, 'owner')
      pushRole(currentAcl?.editors, 'editor')
      pushRole(currentAcl?.viewers, 'viewer')
      setEntries(initial)
    } catch (error) {
      console.error('Failed to load members:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAddMember = (member: OrgMember) => {
    setEntries(prev => [...prev, { ...member, role: 'viewer' }])
  }

  const handleRoleChange = (uid: string, value: string) => {
    if (value === 'remove') {
      setEntries(prev => prev.filter(e => e.uid !== uid))
    } else {
      setEntries(prev => prev.map(e => e.uid === uid ? { ...e, role: value as FolderRole } : e))
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)

      const owners = entries.filter(e => e.role === 'owner').map(e => e.uid)
      const editors = entries.filter(e => e.role === 'editor').map(e => e.uid)
      const viewers = entries.filter(e => e.role === 'viewer').map(e => e.uid)

      if (!firebaseUser || !user?.orgId) return

      const token = await firebaseUser.getIdToken()
      const response = await fetch(`${API_BASE}/v1/organizations/${user.orgId}/folders/${folderId}/acl`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ owners, editors, viewers, visibility })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to update ACL')
      }

      console.log('✅ Folder ACL updated successfully')
      onSuccess()
    } catch (error) {
      console.error('❌ Failed to update folder ACL:', error)
      alert(`権限の更新に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
    >
      <div
        className="modal-card"
        style={{ width: 520, maxHeight: '80vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <h2 className="text-lg font-semibold">
              フォルダを共有
            </h2>
            <p className="text-sm text-secondary mt-1">
              {folderName}
            </p>
          </div>
          <button type="button" className="close-btn" onClick={onClose} title="閉じる">
            <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="modal-body flex-1">
          {loading ? (
            <div className="flex flex-col items-center py-8 gap-2">
              <span className="spinner lg" aria-hidden="true" />
              <p className="text-sm text-secondary">読み込み中...</p>
            </div>
          ) : (
            <>
              {/* メンバー検索・追加 */}
              <div className="mb-4">
                <MemberSearchAdd
                  members={members}
                  excludeUids={entries.map(e => e.uid)}
                  onAdd={handleAddMember}
                  placeholder="メンバーを検索して追加"
                />
              </div>

              {/* アクセスできるメンバー
                  組織全体公開時は「全員が閲覧可」がベースラインになるため、
                  個別行は編集可以上を与える用途のみ（閲覧のみの行は冗長として薄く表示） */}
              {entries.length > 0 && (
                <div className="space-y-1 mb-4">
                  <div className="text-xs font-medium text-tertiary mb-1">
                    {visibility === 'org' ? '追加の権限を持つメンバー' : 'アクセスできるメンバー'}
                  </div>
                  {visibility === 'org' && (
                    <p className="text-xs text-tertiary" style={{ marginBottom: 6 }}>
                      組織全体に公開中は全員が閲覧できます。ここでは編集可・オーナー権限の付与だけが意味を持ちます。
                    </p>
                  )}
                  {entries.map((entry) => {
                    const redundant = visibility === 'org' && entry.role === 'viewer'
                    return (
                    <div key={entry.uid} className="flex items-center gap-3 py-2" style={redundant ? { opacity: 0.5 } : undefined}>
                      <MemberAvatar name={entry.displayName} email={entry.email} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-primary truncate">
                          {entry.displayName}
                          {entry.uid === user?.uid && <span className="text-tertiary">（あなた）</span>}
                        </div>
                        <div className="text-xs text-tertiary truncate">
                          {entry.email}
                          {redundant && <span style={{ marginLeft: 6 }}>・組織公開でカバー済み（指定不要）</span>}
                        </div>
                      </div>
                      <select
                        value={entry.role}
                        onChange={(e) => handleRoleChange(entry.uid, e.target.value)}
                        className="form-select text-sm"
                        style={{ padding: '4px 8px', width: 'auto' }}
                      >
                        <option value="owner">オーナー</option>
                        <option value="editor">編集可</option>
                        <option value="viewer">閲覧可</option>
                        <option value="remove">削除</option>
                      </select>
                    </div>
                    )
                  })}
                </div>
              )}

              {/* 一般的なアクセス */}
              <div style={{ borderTop: '1px solid var(--color-border-light)', paddingTop: 12 }}>
                <div className="text-xs font-medium text-tertiary mb-1">一般的なアクセス</div>
                <div className="flex items-center gap-3 py-2">
                  <span
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: 'var(--color-surface)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    🏢
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-primary">組織のメンバー</div>
                    <div className="text-xs text-tertiary">
                      {visibility === 'org'
                        ? '組織の全員がこのフォルダと中の議事録を見ることができます'
                        : '上のリストに追加されたメンバーだけがこのフォルダを見ることができます'}
                    </div>
                  </div>
                  <select
                    value={visibility}
                    onChange={(e) => setVisibility(e.target.value as FolderVisibility)}
                    className="form-select text-sm"
                    style={{ padding: '4px 8px', width: 'auto' }}
                  >
                    <option value="org">組織全体に公開</option>
                    <option value="restricted">メンバー限定</option>
                  </select>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-outline"
          >
            キャンセル
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="btn btn-primary"
            data-state={saving || loading ? 'disabled' : undefined}
          >
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  )
}
